import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { getRoles } from "../services/roleService";
import { setDynamicPageAccess } from "../auth/roles";

const RolesContext = createContext({
  roles: [],
  loading: true,
  error: "",
  refreshRoles: async () => {},
});

// Shared cache for GET api/roles/ so pages/forms that need the role list
// (Role Management, Employee form dropdowns, etc.) don't each fetch it separately.
export function RolesProvider({ children }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refreshRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const data = await getRoles();
      setRoles(data);
      // Keep the app's live permission map in sync with the latest roles.
      setDynamicPageAccess(data);
    } catch (requestError) {
      console.error("Role Fetch Error:", requestError);
      setError("Unable to fetch roles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshRoles();
  }, [refreshRoles]);

  const value = useMemo(
    () => ({ roles, loading, error, refreshRoles }),
    [roles, loading, error, refreshRoles]
  );

  return (
    <RolesContext.Provider value={value}>
      {children}
    </RolesContext.Provider>
  );
}

export function useRoles() {
  return useContext(RolesContext);
}
